import type { ReactNode } from "react";
import { AgGridReact } from "ag-grid-react";
import type { ColDef, GridOptions } from "ag-grid-community";
import { AllCommunityModule, ClientSideRowModelModule, ModuleRegistry, NumberEditorModule, NumberFilterModule, TextFilterModule, ValidationModule } from "ag-grid-community";
import { AgChartsEnterpriseModule } from "ag-charts-enterprise";
import {
  MasterDetailModule,
  ServerSideRowModelModule,
  SetFilterModule,
  SparklinesModule,
  ContextMenuModule,
} from "ag-grid-enterprise";
import { agGridStyleDriver } from "@/utils/componentStyle";

ModuleRegistry.registerModules([
  MasterDetailModule,
  SetFilterModule,
  ClientSideRowModelModule,
  ServerSideRowModelModule,
  SparklinesModule.with(AgChartsEnterpriseModule),
  ContextMenuModule,
  ValidationModule,
  NumberFilterModule,
  NumberEditorModule,
  TextFilterModule,
  AllCommunityModule
]);

export interface AgGridTableProps<TData extends object = any> {
  /** Column definitions for AG Grid */
  columnDefs: ColDef<TData>[];
  /** Row data to render (omit when using infinite/server-side row models) */
  rowData?: TData[];
  /** Optional grid options override */
  gridOptions?: GridOptions<TData>;
  /** Optional height, defaults to 500px */
  height?: number | string;
  /** Optional class name for the grid container */
  className?: string;
  /** Optional element to render above the grid (e.g. toolbar) */
  header?: ReactNode;
  /** Optional quick filter text passed to the grid */
  quickFilterText?: string;
}

export function AgGridTable<TData extends object = any>({
  columnDefs,
  rowData,
  gridOptions,
  height = 500,
  className = "",
  header,
}: AgGridTableProps<TData>) {
  const isInfiniteOrServerSide =
    gridOptions?.rowModelType === "infinite" ||
    gridOptions?.rowModelType === "serverSide";

  return (
    <div className={`flex flex-col gap-2 ${className}`}>
      {header ? <div>{header}</div> : null}
      <div
        className={agGridStyleDriver.container}
        style={{ height: typeof height === "number" ? `${height}px` : height }}
      >
        <AgGridReact<TData>
          columnDefs={columnDefs}
          // Only pass rowData for client-side row model; for infinite/server-side
          // models, data is provided via the datasource.
          {...(!isInfiniteOrServerSide && rowData ? { rowData } : {})}
          animateRows
          quickFilterText={gridOptions?.quickFilterText as string | undefined}
          {...gridOptions}
        />
      </div>
    </div>
  );
}
